<script setup lang="ts">
import { ref } from "vue"
import type { AddressHistory } from "@/model/backend/address-history.ts"
import { useHttpClient } from "@/vf"

const props = defineProps<{
    customerId?: string
}>()

defineExpose({
    refresh: loadAddresses,
})

type DefaultAddress = {
    firstName: string
    lastName: string
    street: string
    zip: string
    city: string
    additionalAddress?: string
}

const emits = defineEmits<{
    select: [address: AddressHistory]
    delete: [address: AddressHistory]
}>()

const http = useHttpClient()
const addresses = ref<AddressHistory[]>([])
const defaultAddress = ref<DefaultAddress>()

async function loadAddresses() {
    //
    const apiString =
        props.customerId !== undefined
            ? `/address-history/find-history/${props.customerId}`
            : `/address-history/find-history`

    const result = await http.get<{ addresses: AddressHistory[]; defaultAddress: DefaultAddress }>(apiString)
    addresses.value = result.data.addresses
    defaultAddress.value = result.data.defaultAddress
}
loadAddresses()

function selectAddressFromHistory(address: AddressHistory) {
    emits("select", {
        firstName: address.firstName,
        lastName: address.lastName,
        street: address.street,
        zip: address.zip,
        city: address.city,
        additionalAddress: address.additionalAddress,
        deliveryNote: address.deliveryNote,
    })
}

function selectDefaultAddress() {
    emits("select", {
        firstName: defaultAddress.value!.firstName,
        lastName: defaultAddress.value!.lastName,
        street: defaultAddress.value!.street,
        zip: defaultAddress.value!.zip,
        city: defaultAddress.value!.city,
        additionalAddress: defaultAddress.value!.additionalAddress,
        deliveryNote: undefined,
    })
}
</script>

<template>
    <div class="row align-items-stretch">
        <div v-if="defaultAddress" class="col-12 col-sm-6 col-md-4 mb-3">
            <button type="button" class="address-card address-card-customer" @click="selectDefaultAddress">
                <span class="title">Rechnungsadresse verwenden</span>
                <span>{{ defaultAddress!.firstName }} {{ defaultAddress!.lastName }}</span>
                <span>{{ defaultAddress!.street }}</span>
                <span v-if="defaultAddress!.additionalAddress" class="address-card-muted-text">
                    {{ defaultAddress!.additionalAddress }}
                </span>
                <span>{{ defaultAddress!.zip }} {{ defaultAddress!.city }}</span>
            </button>
        </div>

        <div v-for="(address, i) in addresses" :key="i" class="col-12 col-sm-6 col-md-4 mb-3">
            <div class="d-flex h-100">
                <button type="button" class="address-card" @click="selectAddressFromHistory(address)">
                    <span class="title">Vorherige Lieferadresse</span>
                    <span>{{ address.firstName }} {{ address.lastName }}</span>
                    <span>{{ address.street }}</span>
                    <span v-if="address.additionalAddress" class="text-muted">
                        {{ address.additionalAddress }}
                    </span>
                    <span>{{ address.zip }} {{ address.city }}</span>
                    <span v-if="address.deliveryNote" class="text-muted">
                        {{ address.deliveryNote }}
                    </span>
                </button>
                <button
                    v-if="address.id"
                    type="button"
                    class="address-card-delete"
                    aria-label="Vorherige Lieferadresse löschen"
                    @click.stop="emits('delete', address)"
                >
                    <i class="fa fa-fw fa-trash" aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.address-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    text-align: left;
    padding: 0.75rem 1rem;
    border: 1px solid #d4d8d8;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition:
        box-shadow 0.2s ease,
        transform 0.05s ease,
        border-color 0.2s ease;
    line-height: 1.3;
    color: #374151;
    border-radius: 5px 0 0 5px;
}
.address-card:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    border-color: #9e9e9e;
}
.address-card-customer {
    border-radius: 5px;
}
.address-card .title {
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #111827;
}
.address-card-delete {
    height: 100%;
    width: 20%;
    border: #d4d8d8 1px solid;
    background: #89bcb3;
    color: #ffffff;
    font-size: 1.25rem;
    border-radius: 0 5px 5px 0;
    border-left: 0;
}
.address-card-delete:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    border-color: #9e9e9e;
    background: #71a89f;
}
</style>
